import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ListAlt as ListAltIcon, Close as CloseIcon } from "@mui/icons-material";
import ReviewSidebar from "./ReviewSidebar";
import ReviewGroup from "./ReviewGroup";

/**
 * ReviewBody — sidebar (parts → numbered pills) + detail panel.
 *
 * Accepts a normalized `detailedGroups[]` shape:
 *   regular  : { type, partNumber, audioUrl, imageUrl, extraImageUrl, content,
 *               questions: [{ question, userAnswer, isCorrect, answered }] }
 *   dialogue : { type: "dialogue-group", partNumber, audioUrl, imageUrl,
 *               dialogueScript, blanks: [{position, correctAnswers}],
 *               dialogueQuestion: { question, userAnswer: {[pos]: string} },
 *               childQuestions: [{ question, userAnswer, isCorrect, answered }] }
 *
 * Dialogue parents are intentionally excluded from the pill numbering — the
 * parent is a passage with inline blanks, not a standalone question.
 */
const ReviewBody = ({ detailedGroups, useIndexNumbering = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const flatItems = useMemo(() => {
    const list = [];
    detailedGroups.forEach((group, gIdx) => {
      if (group.type === "dialogue-group") {
        (group.childQuestions || []).forEach((child) => {
          if (child?.question) {
            list.push({
              questionId: child.question._id,
              groupIndex: gIdx,
              item: child,
            });
          }
        });
      } else {
        (group.questions || []).forEach((q) => {
          if (q?.question) {
            list.push({
              questionId: q.question._id,
              groupIndex: gIdx,
              item: q,
            });
          }
        });
      }
    });
    return list;
  }, [detailedGroups]);

  const numberMap = useMemo(() => {
    const m = {};
    flatItems.forEach((it, idx) => {
      m[it.questionId] = useIndexNumbering
        ? idx + 1
        : (it.item?.question?.number ?? idx + 1);
    });
    return m;
  }, [flatItems, useIndexNumbering]);

  const parts = useMemo(() => {
    const byPart = new Map();
    flatItems.forEach((it) => {
      const group = detailedGroups[it.groupIndex];
      const p = group?.partNumber ?? 0;
      if (!byPart.has(p)) byPart.set(p, []);
      byPart.get(p).push(it);
    });
    return [...byPart.entries()]
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([partNumber, items]) => ({ partNumber, items }));
  }, [flatItems, detailedGroups]);

  const [selectedGroupIndex, setSelectedGroupIndex] = useState(
    flatItems[0]?.groupIndex ?? 0
  );
  const [selectedQuestionId, setSelectedQuestionId] = useState(
    flatItems[0]?.questionId ?? null
  );
  const [expandedPart, setExpandedPart] = useState(parts[0]?.partNumber ?? 0);

  // If the detailedGroups arrive after mount (async load), seed the selection
  // once a first item becomes available.
  useEffect(() => {
    if (selectedQuestionId == null && flatItems.length > 0) {
      setSelectedGroupIndex(flatItems[0].groupIndex);
      setSelectedQuestionId(flatItems[0].questionId);
      const group = detailedGroups[flatItems[0].groupIndex];
      if (group?.partNumber !== undefined) setExpandedPart(group.partNumber);
    }
  }, [flatItems, selectedQuestionId, detailedGroups]);

  const selectedGroup = detailedGroups[selectedGroupIndex];

  const handlePickItem = (it) => {
    setSelectedGroupIndex(it.groupIndex);
    setSelectedQuestionId(it.questionId);
    const group = detailedGroups[it.groupIndex];
    if (group?.partNumber !== undefined) setExpandedPart(group.partNumber);
    if (isMobile) setDrawerOpen(false);
  };

  if (detailedGroups.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center", width: "100%" }}>
        <Typography color="text.secondary">
          No review data available.
        </Typography>
      </Box>
    );
  }

  const sidebar = (
    <ReviewSidebar
      theme={theme}
      parts={parts}
      numberMap={numberMap}
      expandedPart={expandedPart}
      setExpandedPart={setExpandedPart}
      selectedQuestionId={selectedQuestionId}
      onPickItem={handlePickItem}
      variant={isMobile ? "drawer" : "inline"}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        width: "100%",
        height: "100%",
        minHeight: 0,
        position: "relative",
      }}
    >
      {!isMobile && sidebar}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          height: "100%",
          overflow: "auto",
          p: { xs: 2, sm: 3 },
        }}
      >
        {selectedGroup ? (
          <ReviewGroup
            group={selectedGroup}
            numberMap={numberMap}
            highlightedQuestionId={selectedQuestionId}
            theme={theme}
          />
        ) : (
          <Typography color="text.secondary">
            Select a question to view details.
          </Typography>
        )}
      </Box>

      {isMobile && (
        <>
          <Tooltip title="Question navigator">
            <IconButton
              onClick={() => setDrawerOpen(true)}
              aria-label="Open question navigator"
              sx={{
                position: "fixed",
                bottom: 16,
                right: 16,
                zIndex: (t) => t.zIndex.modal + 1,
                width: 56,
                height: 56,
                borderRadius: "50%",
                background:
                  "linear-gradient(135deg, #0288D1 0%, #01579B 100%)",
                color: "white",
                boxShadow: theme.shadows[6],
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #0277BD 0%, #01467D 100%)",
                },
              }}
            >
              <ListAltIcon />
            </IconButton>
          </Tooltip>
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: { xs: "85vw", sm: 320 },
                maxWidth: 360,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Questions
              </Typography>
              <IconButton
                onClick={() => setDrawerOpen(false)}
                size="small"
                aria-label="close question navigator"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflow: "auto" }}>{sidebar}</Box>
          </Drawer>
        </>
      )}
    </Box>
  );
};

export default ReviewBody;
